import { Button } from "@/components/ui/button"
import { Dropdown2, type Dropdown2Group } from "@/components/ui/dropdown2"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowRight } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  useConfirmMapping,
  useMapping,
} from "../../../api/questionnaireProcessingApi"
import {
  COLUMN_STATE_TRIGGER,
  columnIndexToRef,
  getColumnState,
} from "../../master-data/mappingUtils"
import { FooterActionBar } from "../../shared/FooterActionBar"
import { McPill } from "../../shared/McPill"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { SectionLabel } from "../../shared/SectionLabel"
import { FrameworkChips } from "./FrameworkChips"
import { FrameworkGroupedList } from "./FrameworkGroupedList"
import { useProcessingOutletContext } from "./QuestionnaireProcessingLayout"
import { SheetPreviewPanel, type SheetCellClick } from "./SheetPreviewPanel"
import { useFrameworkGroups } from "./useFrameworkGroups"

const NONE_OPTION = "__none__"
const NON_QUESTION = "__non_question__"

// Column letters restart at every sheet (each sheet's own column_index starts
// back at 1), so a bare letter ref collides across sheets — e.g. sheet A's
// column B and sheet C's column B are different columns but the same ref.
// Key active/selected state off the sheet + source column instead so
// highlighting never bleeds into another sheet's same-lettered column.
function columnKey(column: {
  sourceSheet: string | null
  sourceColumn: string | null
}) {
  return `${column.sourceSheet ?? ""}::${column.sourceColumn ?? ""}`
}

export function MappingStep() {
  const { translate } = useTranslation()
  const { questionnaireId } = useProcessingOutletContext()
  const { data, isLoading } = useMapping(questionnaireId)
  const { mutate: confirmMapping, isPending } = useConfirmMapping()
  const [overrides, setOverrides] = useState<Record<string, string | null>>({})
  const [activeColumnKey, setActiveColumnKey] = useState<string | null>(null)
  const columnListRef = useRef<HTMLDivElement>(null)

  const columns = useMemo(() => data?.columns ?? [], [data])
  const canonicalQuestions = data?.canonicalQuestions

  // Columns the user needs to map (excludes part-identifier columns which are
  // handled automatically by the backend — they stay in the preview but don't
  // need a mapping decision).
  const mappableColumns = useMemo(
    () => columns.filter((c) => !(c.status === "matched" && !c.canonicalId)),
    [columns],
  )

  const fieldGroups: Dropdown2Group[] = useMemo(
    () => [
      {
        key: "fields",
        label: translate("MC_QP_MAP_GROUP_FIELDS"),
        options: [
          { value: NONE_OPTION, label: translate("MC_QP_MAP_DONT_IMPORT") },
          ...(canonicalQuestions ?? []).map((q) => ({
            value: q.canonicalId,
            label: q.framework,
          })),
        ],
      },
    ],
    [canonicalQuestions, translate],
  )

  /** Raw dropdown value for a column: the user's override if one was made
   * (including the `NONE_OPTION` sentinel), otherwise the original AI-suggested
   * `canonicalId`. This is what `Dropdown2` needs as `value` so it can highlight
   * "Don't import" as selected instead of showing nothing.
   * Keyed by `columnKey` (sheet + source column), not bare `sourceColumn` —
   * a header name can repeat across sheets, and keying by name alone would
   * make an override on one sheet's column bleed into another sheet's
   * same-named column. */
  function rawValue(key: string, original: string | null) {
    if (!(key in overrides)) return original
    return overrides[key]
  }

  /** Semantic canonical id for a column: same as `rawValue` but with the
   * `NONE_OPTION` sentinel collapsed to `null`. Use this wherever the actual
   * target question matters (mapped/badge logic, the confirm payload). */
  function resolvedCanonicalId(key: string, original: string | null) {
    if (original === NON_QUESTION) return NON_QUESTION
    const raw = rawValue(key, original)
    return raw === NONE_OPTION ? null : raw
  }

  /** A column is "resolved" once the user has made a decision for it — either
   * it already carries a real canonical id, or an explicit override (including
   * "Don't import") was recorded. */
  function isResolved(key: string, original: string | null) {
    return !!original || key in overrides
  }

  const resolvedCount = mappableColumns.filter((c) =>
    isResolved(columnKey(c), c.canonicalId),
  ).length
  // 0 === 0 is trivially true — a file with no mappable columns (every
  // column auto-handled as a part identifier) has nothing left to resolve,
  // so it must count as complete rather than permanently blocking Confirm.
  const allMapped = resolvedCount === mappableColumns.length

  const mappedCount = mappableColumns.filter((c) =>
    resolvedCanonicalId(columnKey(c), c.canonicalId),
  ).length

  const { groups, registerSection, jumpTo } = useFrameworkGroups(
    mappableColumns,
    (c) => c.sourceSheet,
    translate("MC_QP_FRAMEWORK_OTHER"),
  )

  useEffect(() => {
    if (!activeColumnKey) return
    columnListRef.current
      ?.querySelector(`[data-column-key="${CSS.escape(activeColumnKey)}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [activeColumnKey])

  // Reverse of the active key: look back up the source column so the preview
  // panel can switch to its sheet and highlight its header.
  const activeColumn = useMemo(
    () => columns.find((c) => columnKey(c) === activeColumnKey) ?? null,
    [columns, activeColumnKey],
  )

  const handlePreviewCellClick = (cell: SheetCellClick) => {
    const column = columns.find(
      (c) =>
        c.sourceColumn === cell.header &&
        (c.sourceSheet == null || c.sourceSheet === cell.sheetName),
    )
    if (column) setActiveColumnKey(columnKey(column))
  }

  if (isLoading || !data) {
    return <div className="flex h-full items-center justify-center" />
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="px-6 pb-2 pt-1">
        <ModuleHeader
          title={translate("MC_QP_MAP_TITLE")}
          helpArticle={{
            categoryId: "customer-studio",
            articleId: "customer.requests",
          }}
          subtitle={translate("MC_QP_MAP_SUBTITLE", {
            mapped: resolvedCount,
            total: mappableColumns.length,
            needsReview: mappableColumns.length - resolvedCount,
          })}
        />
      </div>

      <FrameworkChips groups={groups} onJump={jumpTo} />

      <div className="min-h-0 flex-1 px-6 py-4 pb-0">
        <div className="h-full overflow-hidden rounded-lg border">
          <ResizablePanelGroup orientation="horizontal">
            <ResizablePanel defaultSize={42} minSize={28}>
              <div ref={columnListRef} className="flex h-full min-h-0 flex-col">
                <div className="flex items-center justify-between border-b bg-grey1 px-4 py-3">
                  <SectionLabel>
                    {translate("MC_MD_MAP_COLUMN_TO_FIELD")}
                  </SectionLabel>
                  <McPill tone={allMapped ? "success" : "muted"}>
                    {translate("MC_MD_MAP_MAPPED", {
                      assigned: mappedCount,
                      total: mappableColumns.length,
                    })}
                  </McPill>
                </div>

                <ScrollArea className="min-h-0 flex-1">
                  <FrameworkGroupedList
                    groups={groups}
                    registerSection={registerSection}
                    keyOf={(column) => columnKey(column)}
                    renderItem={(column) => {
                      const key = columnKey(column)
                      const dropdownValue = rawValue(key, column.canonicalId)
                      const assignment = resolvedCanonicalId(
                        key,
                        column.canonicalId,
                      )
                      const state = getColumnState(
                        assignment,
                        column.canonicalId,
                      )
                      const columnRef = columnIndexToRef(column.columnIndex - 1)
                      const active = key === activeColumnKey
                      return (
                        <div
                          key={key}
                          data-column-key={key}
                          onClick={() => setActiveColumnKey(key)}
                          className={cn(
                            "flex cursor-pointer items-center gap-3 border-b border-l-2 px-3 py-2 transition-colors",
                            active
                              ? "border-l-primary bg-primary/10"
                              : "border-l-transparent hover:bg-primary/4",
                          )}
                        >
                          <span className="font-mono flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-card text-xs text-muted-foreground">
                            {columnRef}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                            {column.sourceColumn}
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <Dropdown2
                            value={dropdownValue ?? null}
                            onChange={(value) => {
                              setActiveColumnKey(key)
                              setOverrides((prev) => ({
                                ...prev,
                                [key]: value,
                              }))
                            }}
                            groups={fieldGroups}
                            showNoSelection={false}
                            noSelectionLabel={translate(
                              "MC_QP_MAP_SELECT_FIELD",
                            )}
                            searchPlaceholder={translate(
                              "MC_QP_MAP_FIELD_SEARCH_PLACEHOLDER",
                            )}
                            emptyText={translate("MC_QP_MAP_FIELD_EMPTY")}
                            triggerWidth="220px"
                            popoverWidth="280px"
                            triggerClassName={cn(
                              COLUMN_STATE_TRIGGER[state],
                              active && "ring-2 ring-primary/40",
                            )}
                            groupHeadingClassName="[&_[cmdk-group-heading]]:font-mono **:[[cmdk-group-heading]]:text-[10px] **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-widest **:[[cmdk-group-heading]]:text-yellow1"
                          />
                        </div>
                      )
                    }}
                  />
                </ScrollArea>
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={58} minSize={30}>
              <SheetPreviewPanel
                questionnaireId={questionnaireId}
                activeSheetName={activeColumn?.sourceSheet ?? undefined}
                highlightHeader={activeColumn?.sourceColumn ?? null}
                onCellClick={handlePreviewCellClick}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>

      <FooterActionBar
        left={
          <p>
            {translate("MC_QP_MAP_FOOTER_STATUS", {
              mapped: resolvedCount,
              total: mappableColumns.length,
            })}
          </p>
        }
      >
        <Button
          data-agent-target="request.confirm-mapping"
          className="bg-yellow1 text-black hover:bg-yellow2"
          disabled={!allMapped || isPending}
          onClick={() =>
            confirmMapping({
              id: questionnaireId,
              body: {
                columns: mappableColumns.map((c) => ({
                  sourceSheet: c.sourceSheet,
                  sourceColumn: c.sourceColumn ?? "",
                  canonicalId: resolvedCanonicalId(columnKey(c), c.canonicalId),
                })),
              },
            })
          }
        >
          {translate("MC_QP_MAP_CONFIRM_BUTTON")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </FooterActionBar>
    </div>
  )
}
